import { useRef, useCallback } from 'react';

interface UseSwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onTap?: () => void;
  enabled?: boolean;
  /** Minimum horizontal distance in px to trigger a swipe (default: 50) */
  threshold?: number;
}

/**
 * Lightweight touch gesture hook for swipe left/right and tap detection.
 * All tracking is via useRef — no re-renders during the gesture.
 */
export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  onTap,
  enabled = true,
  threshold = 50,
}: UseSwipeGestureOptions) {
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const isSwiping = useRef(false);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      startTime.current = Date.now();
      isSwiping.current = false;
    },
    [enabled],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - startX.current);
      if (deltaX > threshold / 2) {
        isSwiping.current = true;
      }
    },
    [enabled, threshold],
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startX.current;
      const deltaY = Math.abs(touch.clientY - startY.current);
      const elapsed = Date.now() - startTime.current;

      if (Math.abs(deltaX) >= threshold && deltaY <= 100) {
        // Swipe detected
        if (deltaX < 0) {
          onSwipeLeft?.();
        } else {
          onSwipeRight?.();
        }
      } else if (!isSwiping.current && elapsed < 300) {
        // Short tap with no significant movement
        onTap?.();
      }
    },
    [enabled, threshold, onSwipeLeft, onSwipeRight, onTap],
  );

  return { onTouchStart, onTouchMove, onTouchEnd };
}
